import { useCallback, useEffect, useState } from 'react';
import {
  ExperimentFlag,
  GetFeatureExperimentsResponse
} from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { SaveChangesInput } from '../../Components/ModalEditWidget/modal-edit-constants';
import { useSetError } from '../../redux/modals';
import { awaitJSON, UsersLambdaFetch } from '../../utils/fetch-utils';

type FrontendRow = ExperimentFlag & { id: string };

const toExperimentFlag = (row: FrontendRow): ExperimentFlag => ({
  experiment_flag: row.experiment_flag,
  description: row.description,
  created: row.created,
  lastmodified: row.lastmodified,
  lastuser: row.lastuser,
  disable_ui_control: row.disable_ui_control
});

export const useExperimentFlagTable = () => {
  const [initialTableData, setInitialTableData] = useState<FrontendRow[]>([]);
  const [originalDataMap, setOriginalDataMap] = useState<Record<string, FrontendRow>>({});
  const [loading, setLoading] = useState(true);
  const setError = useSetError();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await UsersLambdaFetch('/getFeatureExperiments');
      const data = await awaitJSON<GetFeatureExperimentsResponse>(res);
      const experiments: FrontendRow[] = data.experiment_flags.map((row, idx) => ({
        ...row,
        id: String(idx)
      }));
      const map: Record<string, FrontendRow> = {};
      for (const e of experiments) map[e.id] = e;
      setInitialTableData(experiments);
      setOriginalDataMap(map);
    } catch (e) {
      setError({
        message: `Error fetching experiment flag data: ${e.message}`,
        reportError: e
      });
    } finally {
      setLoading(false);
    }
  }, [setError]);

  const onSaveModal = useCallback(
    async ({ addedData, modifiedData, deletedData, cleanup }: SaveChangesInput<FrontendRow>) => {
      try {
        const editedDeletes = modifiedData.map(row => originalDataMap[row.id]);
        const allInserts: ExperimentFlag[] = [...addedData, ...modifiedData].map(toExperimentFlag);
        const allDeletes: ExperimentFlag[] = [...deletedData, ...editedDeletes].map(
          toExperimentFlag
        );
        await UsersLambdaFetch('/updateFeatureExperiments', {
          method: 'POST',
          body: {
            experiment_flags: { insert: allInserts, delete: allDeletes }
          }
        });
        cleanup();
        await refresh();
      } catch (e) {
        const message = e.message.includes('violates foreign key constraint')
          ? 'You cannot edit or delete an experiment flag until it is removed from both the User Experiment and Company Experiment tables'
          : `Failed to save experiment flag data: ${e.message}`;
        setError({ message, reportError: e });
      }
    },
    [originalDataMap, refresh, setError]
  );

  useEffect(() => {
    const init = async () => {
      await refresh();
    };
    init();
  }, [refresh]);

  return { initialTableData, onSaveModal, loading };
};
